import { Container, IDestroyOptions } from "pixi.js";
import { gsap } from "gsap/gsap-core";
import { CircleScrollItem } from "./CircleScrollItem";

export class UpgradeCircleScroll extends Container {
    private readonly itemsContainer: Container;
    private readonly radius: number;
    private readonly items: Array<CircleScrollItem>;
    private readonly scrollTimeline: gsap.core.Timeline;
    private currentItemIndex: number;

    public get itemIndex(): number {
        return this.currentItemIndex;
    }

    public get scrollItems(): ReadonlyArray<CircleScrollItem> {
        return this.items;
    }

    public constructor(radius: number) {
        super();
        this.items = [];
        this.radius = radius;
        this.scrollTimeline = new gsap.core.Timeline({ paused: true });
        this.itemsContainer = new Container();
        this.itemsContainer.position.set(0, radius);
        this.addChild(this.itemsContainer);
        this.currentItemIndex = -1;
    }

    public addItem<T extends CircleScrollItem[]>(...items: T): T[0] {
        this.items.push(...items);

        const angleOffset = Math.PI * 2 / this.items.length;

        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const angle = Math.PI / 2 - i * angleOffset;
            item.pivot.x = item.width * 0.5;
            item.position.set(this.radius * Math.cos(angle), this.radius * Math.sin(angle));
            this.itemsContainer.addChild(item);

            const scale = Math.min(Math.max(Math.sin(angle), 0.33) + 0.33, 1.0);
            item.scale.set(scale, scale);
        }

        if (this.currentItemIndex === -1 && this.items.length > 0) {
            this.currentItemIndex = 0;
        }

        return this.items[0];
    }

    public async showScrollToAnimation(itemIndex: number): Promise<void> {
        if (this.currentItemIndex === itemIndex) {
            return;
        }

        this.scrollTimeline.clear();
        const rotationAngle = itemIndex * Math.PI * 2 / this.items.length;
        const offset = ((rotationAngle === 0 ? Math.abs(this.itemsContainer.rotation) / this.itemsContainer.rotation * Math.PI * 2 : rotationAngle) - this.itemsContainer.rotation) % (Math.PI * 2);
        const angle = offset > Math.PI ? offset - Math.PI * 2 : offset < -Math.PI ? Math.PI * 2 + offset : offset;
        const endAngle = this.itemsContainer.rotation + angle;

        this.scrollTimeline.to(this.itemsContainer, { rotation: endAngle, duration: 2.0, ease: "back.inOut(2)" });
        const animations = this.items.map(item => item.startRotation(Math.abs(angle) / angle * -Math.PI / 6));

        const angleOffset = Math.PI * 2 / this.items.length;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemAngle = (endAngle !== 0 && Math.abs(endAngle) / endAngle === -1 ? Math.PI * 2 : 0) + Number(((endAngle % (Math.PI * 2) + Math.PI / 2 - i * angleOffset)).toPrecision(7));
            if (Number(itemAngle.toPrecision(7)) === Number((Math.PI / 2).toPrecision(7))) {
                this.scrollTimeline.to(item.scale, { x: 1, y: 1, duration: 1.25, ease: "back.in(1)" }, "<");
                continue;
            }
            const scale = Math.min(Math.max(Math.sin(itemAngle), 0.33) + 0.33, 0.85);
            this.scrollTimeline.to(item.scale, { x: scale, y: scale, duration: 1.25, ease: "back.in(1)" }, "<");
        }

        this.currentItemIndex = itemIndex;
        await Promise.all([this.scrollTimeline.play(), ...animations]);
        this.itemsContainer.rotation = this.itemsContainer.rotation % Number((Math.PI * 2).toPrecision(7));
    }

    public async showFullScrollAnimation(direction: FullScrollAnimationDirection, startIndex: number | undefined = undefined): Promise<void> {
        this.scrollTimeline.clear();

        const fromIndex = startIndex ?? this.currentItemIndex;
        const startAngle = fromIndex * Math.PI * 2 / this.items.length;
        const endAngle = startAngle + direction * Math.PI * 2;

        this.scrollTimeline.fromTo(this.itemsContainer, { rotation: startAngle }, { rotation: endAngle, duration: 2.0, ease: "back.inOut(2)" });
        const animations = this.items.map(item => item.startRotation(direction * -Math.PI / 6));

        const angleOffset = Math.PI * 2 / this.items.length;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemAngle = (endAngle === 0 || Math.abs(endAngle) / endAngle === -1 ? Math.PI * 2 : 0) + Number(((endAngle % (Math.PI * 2) + Math.PI / 2 - i * angleOffset)).toPrecision(7));
            if (Number((itemAngle % Number((Math.PI * 2).toPrecision(7))).toPrecision(7)) === Number((Math.PI / 2).toPrecision(7))) {
                this.scrollTimeline.to(item.scale, { x: 1, y: 1, duration: 1.25, ease: "back.in(1)" }, "<");
                continue;
            }
            const scale = Math.min(Math.max(Math.sin(itemAngle), 0.33) + 0.33, 0.85);
            this.scrollTimeline.to(item.scale, { x: scale, y: scale, duration: 1.25, ease: "back.in(1)" }, "<");
        }

        this.currentItemIndex = startIndex;
        await Promise.all([this.scrollTimeline.play(), ...animations]);
        this.itemsContainer.rotation = this.itemsContainer.rotation % Number((Math.PI * 2).toPrecision(7));
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.scrollTimeline.kill();
        super.destroy(options);
    }
}

type FullScrollAnimationDirection = -1 | 1;